/**
 * Shared labels and date helpers for the Volunteer Portal.
 */

export const ACTION_STATUSES = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'done', label: 'Done' },
  { value: 'cancelled', label: 'Cancelled' },
];

export const OPEN_STATUSES = ['open', 'in_progress', 'blocked'];

export const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
];

export const MEETING_STATUSES = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'held', label: 'Held' },
  { value: 'cancelled', label: 'Cancelled' },
];

export function statusLabel(value) {
  return ACTION_STATUSES.find((s) => s.value === value)?.label || value;
}

export function priorityLabel(value) {
  return PRIORITIES.find((p) => p.value === value)?.label || value;
}

// Dates from the API are plain YYYY-MM-DD. Parse them as local noon so they
// never slide a day across a timezone boundary.
function parseDateOnly(value) {
  if (!value) return null;
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 12);
}

export function formatDate(value, opts) {
  const date = parseDateOnly(value);
  if (!date) return '—';
  return date.toLocaleDateString('en-US', opts || { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatShortDate(value) {
  return formatDate(value, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatTime(value) {
  if (!value) return '';
  const [h, m] = String(value).split(':').map(Number);
  if (Number.isNaN(h)) return '';
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m || 0).padStart(2, '0')} ${suffix}`;
}

export function todayString() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export function isOverdue(action) {
  return (
    OPEN_STATUSES.includes(action.status) &&
    action.due_date &&
    action.due_date < todayString()
  );
}

// "in 5 days" / "3 days ago" — used for due-date urgency.
export function relativeDays(value) {
  const date = parseDateOnly(value);
  if (!date) return '';
  const today = parseDateOnly(todayString());
  const days = Math.round((date - today) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  return days > 0 ? `in ${days} days` : `${Math.abs(days)} days overdue`;
}

export function segmentLabel(action) {
  if (!action.segment_id) return null;
  if (!action.segment_trail_name) return 'Linked segment';
  if (action.segment_label) return action.segment_label;
  return action.segment_number
    ? `${action.segment_trail_name} #${action.segment_number}`
    : action.segment_trail_name;
}

// The committee's standing cadence. Mirrors backend/src/utils/meetingSchedule.js
// so the scheduling form can warn about an off-cycle date before submitting.
export function isSecondTuesday(dateStr) {
  const [y, m, d] = String(dateStr || '').slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return true;
  const first = new Date(Date.UTC(y, m - 1, 1));
  const daysToFirstTuesday = (2 - first.getUTCDay() + 7) % 7;
  return d === 1 + daysToFirstTuesday + 7;
}
